import React, { useState, useMemo, useCallback } from 'react';
import { FiCalendar, FiClock, FiCheckCircle, FiXCircle, FiChevronUp, FiChevronDown } from 'react-icons/fi';

const STATUS_ICONS = {
  applied: <FiClock className="h-4 w-4 text-blue-500" />,
  interviewing: <FiClock className="h-4 w-4 text-yellow-500" />,
  offered: <FiCheckCircle className="h-4 w-4 text-green-500" />,
  rejected: <FiXCircle className="h-4 w-4 text-red-500" />
};

const ListView = React.memo(({ applications, onRefresh, highlightedApplicationId }) => {
  const [sortField, setSortField] = useState('appliedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = useCallback((field) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const sortedApplications = useMemo(() => [...applications].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'jobTitle':
        comparison = (a.jobTitle || '').localeCompare(b.jobTitle || '');
        break;
      case 'company':
        comparison = (a.company || '').localeCompare(b.company || '');
        break;
      case 'status':
        comparison = (a.status || '').localeCompare(b.status || '');
        break;
      case 'appliedAt':
        comparison = new Date(a.appliedAt || 0) - new Date(b.appliedAt || 0);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  }), [applications, sortField, sortDirection]);

  return (
    <div className="card overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th 
              scope="col" 
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('jobTitle')}
              aria-sort={sortField === 'jobTitle' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              aria-label="Sort by job title"
            >
              <div className="flex items-center">
                Job Title
                {sortField === 'jobTitle' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? (
                      <FiChevronUp className="h-4 w-4" />
                    ) : (
                      <FiChevronDown className="h-4 w-4" />
                    )}
                  </span>
                )}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('company')}
              aria-sort={sortField === 'company' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              aria-label="Sort by company"
            >
              <div className="flex items-center">
                Company
                {sortField === 'company' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? (
                      <FiChevronUp className="h-4 w-4" />
                    ) : (
                      <FiChevronDown className="h-4 w-4" />
                    )}
                  </span>
                )}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('status')}
              aria-sort={sortField === 'status' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              aria-label="Sort by status"
            >
              <div className="flex items-center">
                Status
                {sortField === 'status' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? (
                      <FiChevronUp className="h-4 w-4" />
                    ) : (
                      <FiChevronDown className="h-4 w-4" />
                    )}
                  </span>
                )}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('appliedAt')}
              aria-sort={sortField === 'appliedAt' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              aria-label="Sort by applied date"
            >
              <div className="flex items-center">
                Applied Date
                {sortField === 'appliedAt' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? (
                      <FiChevronUp className="h-4 w-4" />
                    ) : (
                      <FiChevronDown className="h-4 w-4" />
                    )}
                  </span>
                )}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedApplications.length > 0 ? (
            sortedApplications.map((application) => (
              <tr 
                key={application.id}
                className={highlightedApplicationId === application.id ? 'bg-primary-50 animate-pulse' : ''}
              >
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 break-words max-w-xs">{application.jobTitle}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500 max-w-[150px] truncate" title={application.company}>{application.company}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {STATUS_ICONS[application.status]}
                    <span className="ml-1 text-sm text-gray-500 capitalize">{application.status}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-500">
                    <FiCalendar className="mr-1 h-4 w-4" />
                    {application.appliedAt ? new Date(application.appliedAt).toLocaleDateString() : '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => application.onView(application)}
                    aria-label="View application"
                    className={`text-primary-600 hover:text-primary-900 mr-4 ${
                      highlightedApplicationId === application.id ? 'font-bold' : ''
                    }`}
                  >
                    View
                  </button>
                  <button
                    onClick={() => application.onDelete(application)}
                    aria-label="Delete application"
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                No applications found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

export default ListView;